import { TextLinkWidget } from '@tableau/widgets';
import { useField, useFormikContext } from 'formik';
import React from 'react';
import _ from 'underscore';

import { runNowEnabledSiteDialogProps } from '~src/app/content/RunNowEnabledSiteDialog';
import { SiteSettingsStyles } from '~src/app/content/styles/SiteSettingsStyles';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { ISiteSettingsForServerAdmin } from '~src/settings/types/ISite';
import { L10n } from '~src/ts/L10n/L10n';
import { GetSitesRequest, IGetSitesResult } from '~src/ts/ServerApi/requests/GetSitesRequest';
import { IEditableServerSettings } from '~src/ts/ServerApi/types/IEditableServerSettings';
import { IPage } from '~src/ts/ServerApi/types/IPage';

import { SessionUserSelectors } from '../../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog } from '../../../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../../../commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '../../../../../ts/FeatureFlags';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

/**
 * Map of all sites with run now setting value
 */
interface ISiteRunNowEnabledMap {
  [site: string]: boolean;
}

/**
 * Map of Site Name : Site Id
 */
interface ISiteIDMap {
  [site: string]: string;
}

/**
 * Server Setting Section
 * */
export interface RunNowSitesEnabledSettingProps {
  sitesEnabled: ISiteRunNowEnabledMap;
}

export interface RunNowServerSettingSectionProps {
  settings: Pick<IEditableServerSettings, 'runNowEnabled'> & RunNowSitesEnabledSettingProps;
}

/**
 * We were using UpdateBooleanServerSettingRequest.ts for storing these names.
 * However as we decouple, each setting section should have its own setting name
 * Hence added here
 */
export const UpdateBooleanServerSettingNames = {
  runNowEnabled: 'runNowEnabled',
};

export const RUN_NOW_SERVER_SECTION_CHECKBOX_TEST_ID = 'run-now-enable-checkbox';
export const RUN_NOW_SERVER_SETTING_FIELD = 'settings.runNowEnabled';
export const RUN_NOW_SITES_ENABLED_SERVER_SETTING = 'settings.sitesEnabled';

function getEditSiteSettingDescription(
  isGloballyEnabled: boolean,
  isEnabledForSiteMap: ISiteRunNowEnabledMap): string {

  const sitesRunNowStatus = Object.values(isEnabledForSiteMap);
  const numberOfSitesEnabled = sitesRunNowStatus.filter(enabled => enabled).length;

  const allSiteEnabled = numberOfSitesEnabled === sitesRunNowStatus.length;

  const editSitesDescription = isGloballyEnabled
    ? allSiteEnabled
      ? Messages.runNowGlobalToggleDialogAllSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled) })
      : Messages.runNowGlobalToggleDialogSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(numberOfSitesEnabled) })
    : Messages.runNowGlobalToggleDialogSitesAllowed({ SITES_COUNT_LOC: L10n.localizedCount(0) });
  return editSitesDescription;
}

// Exported for tests
export async function getAllSitesSettings(): Promise<ISiteSettingsForServerAdmin[]> {
  let sites: ISiteSettingsForServerAdmin[] = [];
  let moreItems = true;
  while (moreItems) {
    const page: IPage = {
      startIndex: sites.length,
      maxItems: 100,
    };
    const request = new GetSitesRequest({page});
    const response: IGetSitesResult = await WebHttpClientWithErrorToastInstance.sendRequest(request);
    sites = sites.concat(response.sites);
    moreItems = response.totalCount > sites.length;
  }
  return sites;
}

const isRunNowServerSettingsVisible = (isSessionServerAdmin: boolean): boolean => {
  // ServerSetting Section should only be visible by Server Admins
  return FeatureFlags.enableRunNowEnabled && isSessionServerAdmin;
};

export function RunNowServerSettingSection(props: RunNowServerSettingSectionProps): JSX.Element {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const formik = useFormikContext<RunNowServerSettingSectionProps>();

  const isGloballyEnabled = formik.values.settings.runNowEnabled;
  const isEnabledForSiteMapInitial = React.useRef<ISiteRunNowEnabledMap>({});

  const [isEnabledForSiteMap, setIsEnabledForSiteMap] = React.useState<ISiteRunNowEnabledMap>({});
  const [siteNameToIDMap, setSiteNameToIDMap] = React.useState<ISiteIDMap>({});

  React.useEffect(() => {
    // if the submit count gone up and the form is not dirty that means user clicked 'save'
    // so save our local state to be initial so that the user could change the same setting
    // otherwise it will diff against the very initial and incorrect state
    if (!formik.dirty && isEnabledForSiteMapInitial.current) {
      isEnabledForSiteMapInitial.current = isEnabledForSiteMap;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.dirty]);

  // Fetch RunNow site setting for all sites on server
  React.useEffect(() => {
    if (isRunNowServerSettingsVisible(isServerAdmin)) {
      let isMounted = true;
      getAllSitesSettings().then((allSiteSettings) => {
        if (isMounted) {
          let isEnabledForSiteMap: ISiteRunNowEnabledMap = {};
          let siteNameToIDMap: ISiteIDMap = {};
          for (let site of allSiteSettings) {
            const settings = (site as ISiteSettingsForServerAdmin).settings;
            isEnabledForSiteMap[settings.name] = settings.runNowEnabled;
            siteNameToIDMap[settings.name] = (site as ISiteSettingsForServerAdmin).id;
          }

          setIsEnabledForSiteMap(isEnabledForSiteMap);
          isEnabledForSiteMapInitial.current = isEnabledForSiteMap;
          setSiteNameToIDMap(siteNameToIDMap);
        }
      });
      return () => { isMounted = false; };
    }
  }, [isServerAdmin]);

  const editSitesDescription = getEditSiteSettingDescription(
    isGloballyEnabled, isEnabledForSiteMap
  );

  const [_field, _meta, helpers] = useField<ISiteRunNowEnabledMap>(RUN_NOW_SITES_ENABLED_SERVER_SETTING);
  const showEditSitesDialog = React.useCallback(() => {
    if (!isGloballyEnabled) {
      return;
    }
    const modal = ReactModalService.open(runNowEnabledSiteDialogProps({
      runNowEnabledStates: isEnabledForSiteMap,
      updateSites: (updatedEnabledForSite: ISiteRunNowEnabledMap) => {
        setIsEnabledForSiteMap(updatedEnabledForSite);

        // Only save the changed sites from original state to formik
        // by filtering out the changed sites and then mapping to site id from name
        // since the API requires ids
        let pairs = _.pairs(updatedEnabledForSite);
        pairs = pairs.filter((pair: [string, boolean]) => {
          return isEnabledForSiteMapInitial.current[pair[0]] !== pair[1];
        });
        pairs = pairs.map((pair: [string, boolean]) => {
          return [siteNameToIDMap[pair[0]], pair[1]];
        });
        const mergedEnabledForSite = _.object(pairs);

        // let formik form know that we've made changes and enable the save button
        helpers.setValue(mergedEnabledForSite);
      },
      updateAll: (_state) => {/** unused */},
      close: () => { modal.close(); },
    }));
  }, [isGloballyEnabled, isEnabledForSiteMap, setIsEnabledForSiteMap, helpers, isEnabledForSiteMapInitial, siteNameToIDMap]);

  if (!isRunNowServerSettingsVisible(isServerAdmin)) {
    return null;
  }

  function openConfirmationDialogForServerSetting(event: React.ChangeEvent<HTMLInputElement>) {
    const isRunNowGloballyEnabled = event.target.value === 'true';
    const numberOfSitesEnabled =
      Object.values(isEnabledForSiteMap).filter(enabled => enabled).length;

    const confirmGlobalToggleTitle = isRunNowGloballyEnabled
      ? Messages.runNowDisable()
      : Messages.runNowEnable();

    const confirmGlobalToggleText = isRunNowGloballyEnabled
      ? Messages.runNowDisableConfirmationText({ NUMBER_OF_SITES_ENABLED_LOC: L10n.localizedCount(numberOfSitesEnabled) })
      : Messages.runNowEnableConfirmationText({ NUMBER_OF_SITES_ENABLED_LOC: L10n.localizedCount(numberOfSitesEnabled) });

    const dialogPromiseResult = openReactConfirmationDialog({
      title: confirmGlobalToggleTitle,
      message: confirmGlobalToggleText,
      actionName: Messages.yes(),
      action: () => Promise.resolve(),
      dialogWidth: TabDialogWidth.width375px,
      paramsForAction: null,
    }).result;

    return dialogPromiseResult;
  }

  return (
    <SettingsSection title={Messages.runNowEnabledSettingHeader()} description={Messages.runNowEnabledSettingDescription()} testId={'run-now-setting-section'}>
      <FormikCheckbox
        label={Messages.runNowGlobalToggleDialogCheckboxLabel()}
        name={RUN_NOW_SERVER_SETTING_FIELD}
        testId={RUN_NOW_SERVER_SECTION_CHECKBOX_TEST_ID}
        beforeChange={openConfirmationDialogForServerSetting}
      />
      <p>{editSitesDescription}
        <b>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <TextLinkWidget style={!isGloballyEnabled ? SiteSettingsStyles.DisabledLink : {}} onClick={showEditSitesDialog} href='javascript:void(0)' testId={'run-now-edit-sites-dialog'}>
              {Messages.runNowGlobalToggleDialogLinkText()}
          </TextLinkWidget>
        </b>
      </p>
    </SettingsSection>
  );
}

export const RunNowServerSettingSpec = componentRegistryEntry({
  name: 'RunNowServerSettingSection',
  component: RunNowServerSettingSection,
  onSubmit: async (settings) => {
    const serverSettings = ServerSettingsInstance;
    const promises = [];

    if (settings.sitesEnabled) {
      const sitesToEnable: string[] = [];
      const sitesToDisable: string[] = [];
      _.each(settings.sitesEnabled, (runNowEnabled, siteId) => {
        if (runNowEnabled) {
          sitesToEnable.push(siteId);
        } else {
          sitesToDisable.push(siteId);
        }
      });
      if (sitesToEnable.length > 0) {
        promises.push(serverSettings.updateRunNowSettings(sitesToEnable, true));
      }
      if (sitesToDisable.length > 0) {
        promises.push(serverSettings.updateRunNowSettings(sitesToDisable, false));
      }
    }

    if (settings.runNowEnabled !== undefined) {
      promises.push(ServerSettingsInstance.updateBoolean(
        UpdateBooleanServerSettingNames.runNowEnabled,
        settings.runNowEnabled));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }
});